<template>
    <el-select ref="contact-lists-selector"
               popper-class="contact-lists-selector"
               v-model="contact_list_id"
               class="w-full"
               remote
               v-loading="loading"
               :placeholder="placeholder"
               :multiple="multiple"
               :clearable="clearable"
               :collapse-tags="!no_collapse"
               :size="size"
               :remote-method="getContactLists"
               filterable
               default-first-option
               @change="selectContactList"
               @visible-change="onDropdownVisibleChange">
        <el-option v-for="contact_list in contactListOrdered"
                   :key="contact_list.id"
                   :label="contact_list.name"
                   :value="contact_list.id">
            <span class="ml-2">{{ contact_list.name }}</span>
        </el-option>
  </el-select>
</template>

<script>
import auth from '../auth'
import * as ContactListModuleTypes from '../constants/contact-list-module-types'
import * as ContactListTypes from '../constants/contact-list-types'
import { acl_mixin } from '../mixins'
import { mapState } from 'vuex'
import { clone, debounce } from 'lodash'

export default {
    mixins: [
        acl_mixin
    ],

    data() {
        return {
            auth: auth,
            contact_list_id: this.value,
            loading: false,
            contact_lists: [],
            params: {
                page: 1,
                size: 100,
                user_id: this.user_id,
                sort: 'contact_lists.name',
                order: 'asc',
                include_default_queue: true,
                list_type: this.list_type,
            },
            search_query: null,
            scroll_disabled: false
        }
    },

    props: {
        value: {
            required: false,
            type: [Number, String, Array]
        },

        use_first_list: {
          required: false,
          type: Boolean,
          default: true
        },

        user_id: {
            required: false,
            type: Number,
            default: null
        },

        module_type: {
            required: false,
            default: ContactListModuleTypes.CONTACT_LIST,
            validator (value) {
                return [ContactListModuleTypes.CONTACT_LIST, ContactListModuleTypes.POWER_DIALER_LIST, ContactListModuleTypes.PUBLIC_CONTACT_LIST].includes(value)
            }
        },

        list_type: {
          required: false,
          default: null,
          validator (value) {
            return [ContactListTypes.STATIC_LIST, ContactListTypes.DYNAMIC_LIST].includes(value)
          }
        },

        multiple: {
            type: Boolean,
            required: false,
            default: false
        },

        no_collapse: {
            type: Boolean,
            required: false,
            default: false
        },

        clearable: {
            type: Boolean,
            required: false,
            default: false
        },

        size: {
            type: String,
            default: '',
            required: false
        },

        list_type: {
            type: Number,
            required: false,
            default: null
        },
    },

    computed: {
        ...mapState('cache', [
            'current_company'
        ]),

        placeholder() {
            return this.multiple ? 'Select Contact Lists' : 'Select Contact List'
        },

        endpoint() {
            switch (this.module_type) {
                case ContactListModuleTypes.CONTACT_LIST:
                    return '/api/v2/contacts-list'
                case ContactListModuleTypes.POWER_DIALER_LIST:
                    return '/api/v2/power-dialer-lists'
                case ContactListModuleTypes.PUBLIC_CONTACT_LIST:
                    return '/api/v2/contacts-list/public'
            }
        },

        contactListOrdered() {
            let contact_lists = clone(this.contact_lists)

            return contact_lists.sort((a, b) => {
                // "My Queue" lists should come first
                if (a.type === 0 && b.type !== 0) {
                    return -1
                }
                if (a.type !== 0 && b.type === 0) {
                    return 1
                }

                let textA = a.name.toUpperCase()
                let textB = b.name.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
        }
    },

    mounted() {
        this.getContactLists()

        this.getDebouncedContactLists = debounce(this.getContactLists, 500)
    },

    methods: {
        selectContactList(list) {
            this.contact_list_id = list
            this.$emit('change', list, this.contact_lists)
        },

        addList(list) {
            this.contact_lists.push(list)
            this.contact_list_id = list.id
            this.$emit('change', list.id)
        },

        async getContactLists(search = null, page = 1) {
            if (!this.hasPermissionTo('list contact list item')) {
                return
            }

            this.loading = true

            this.search_query = search
            this.params.page = page

            if (this.list_type) {
                this.params.list_type = this.list_type
            }

            const params = {
                ...this.params,
                page,
                search
            }

            const res = await axios.get(this.endpoint, { params })
            if (page > 1) {
                this.contact_lists.push(...res.data.data)
            } else {
                this.contact_lists = res.data.data
            }
            this.scroll_disabled = this.contact_lists.length >= res.data.total

            this.loading = false

            // After fetching the contact lists, set the first one(My Queue) if none is selected
            if (this.use_first_list && !this.contact_list_id && this.contactListOrdered.length > 0 && this.contactListOrdered[0]?.id) {
                this.selectContactList(this.contactListOrdered[0].id)
            }
        },

        handleScroll(event) {
            const { scrollTop, scrollHeight, clientHeight } = event.target;

            if (scrollTop + clientHeight >= scrollHeight - 10 && !this.scroll_disabled && !this.loading) {
                this.getDebouncedContactLists(this.search_query, this.params.page + 1)
            }
        },
                       
        // Enable/disable scrolling listener when dropdown visibility changed
        onDropdownVisibleChange(visible) {
            const dropdownClass = `.contact-lists-selector .el-select-dropdown__wrap`;
            this.$nextTick(() => {
                const dropdown = document.querySelector(dropdownClass);
                if (!dropdown) {
                    return
                }

                visible
                    ? dropdown.addEventListener('scrollend', (event) => this.handleScroll(event))
                    : dropdown.removeEventListener('scrollend', (event) => this.handleScroll(event))
            });
        }
    },

    watch: {
        value() {
            this.contact_list_id = this.value
        },

        user_id(value) {
            this.params.page = 1
            this.params.user_id = value
            this.$set(this, 'contact_lists', [])
            this.contact_list_id = null
            this.getContactLists(this.search_query, 1)
        }
  }
}
</script>
